import { useReducer, useCallback, useRef, useEffect } from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"
import { DATE_FORMAT } from "src/utils/constants"
import { formatCpfCnpj } from "src/utils/format-cpf-cnpj"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
}

const tableDefault = [
    {
        cells: ["Razão Social: ", "CNPJ: "],
    },
    {
        cells: ["Setor: ", "Subsetor: "],
    },
]

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

export const useRatingDevedorTable = (
    emissaoService,
    cpfCnpj,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    // garante que os dados do cetip anterior não preencham a tabela ao mudar o cetip
    const loadedRef = useRef(cpfCnpj)

    useEffect(() => {
        if (loadedRef.current === cpfCnpj) return

        loadedRef.current = cpfCnpj

        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: tableDefault })
        dispatch({ type: "CLOSE_ALERT" })
        
    }, [cpfCnpj])

    const buildTable = useCallback(
        (razaoSocial, setor, subsetor) => {
            dispatch({
                type: "SET_TABLE",
                payload: [
                    {
                        cells: [
                            `Razão Social: ${razaoSocial || ""}`,
                            `CNPJ: ${formatCpfCnpj(cpfCnpj) || ""}`,
                        ],
                    },
                    {
                        cells: [
                            `Setor: ${setor || ""}`,
                            `Subsetor: ${subsetor || ""}`,
                        ],
                    },
                ],
            })
        },
        [cpfCnpj]
    )

    const loadTableRows = useCallback(() => {
        if (!cpfCnpj) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CPFCNPJ não informado.",
                    type: "warning",
                },
            })
            return
        }

        dispatch({ type: "SET_LOADING", payload: true })
        emissaoService
            .ratingDevedor("get", `?CPFCNPJ=${cpfCnpj}`)
            .then(({ body }) => {
                const { results } = body

                if (!results || results.length === 0) {
                    dispatch({
                        type: "SET_ALERT",
                        payload: {
                            open: true,
                            message: "Não há dados para o CETIP consultado.",
                            type: "warning",
                        },
                    })
                    dispatch({ type: "SET_ROWS", payload: [] })
                    dispatch({ type: "SET_TABLE", payload: tableDefault })
                } else {
                    dispatch({ type: "CLOSE_ALERT" })

                    const rows = ordenaPorDataReferencia(results, "desc").map(
                        (obj, i) => ({ ...obj, id: i })
                    )

                    const { Setor, SubSetor, Devedor, RazaoSocial } = rows[0] || {}
                    buildTable(Devedor || RazaoSocial, Setor, SubSetor)
                    
                    dispatch({ type: "SET_ROWS", payload: rows })
                }
            })
            .catch(() => {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })
                dispatch({ type: "SET_TABLE", payload: tableDefault })
            })
            .finally(() => dispatch({ type: "SET_LOADING", payload: false }))
    }, [cpfCnpj, emissaoService, buildTable])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]

        if (state.rows.length === 0) {
            lastItem = {
                DataReferencia: dayjs().format(DATE_FORMAT),
                CPFCNPJ: cpfCnpj,
                DataValidade: dayjs().format(DATE_FORMAT),
                Devedor: dadosEmpresa?.devedor || "N/A",
                Setor: "N/A",
                SubSetor: "N/A",
                Fonte: "N/A",
                ScoreRating: 0,
                Rating: "N/A",
            }
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await emissaoService.ratingDevedor("post", lastItem)
            loadTableRows()
        } catch (error) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cpfCnpj, dadosEmpresa, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            if (!row || !row.ID) {
                return
            }

            const { ID } = row

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingDevedor("delete", ID)
                loadTableRows()
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const { ID } = row

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingDevedor("patch", ID, row)

                const updatedRows = state.rows.map((existingRow) =>
                    existingRow.ID === ID
                        ? { ...existingRow, ...row }
                        : existingRow
                )
                dispatch({ type: "SET_ROWS", payload: updatedRows })

                return row
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
                throw error
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, state.rows]
    )

    return {
        state: {
            ...state,
            minWidthAnaliseCredito: calculateMinWidth(
                state.rows,
                "AnaliseCreditoDevedor"
            ),
            minWidthObservacao: calculateMinWidth(state.rows, "Observacao"),
        },
        actions: {
            loadTableRows,
            setAlert: (payload) => dispatch({ type: "SET_ALERT", payload }),
            closeAlert: () => dispatch({ type: "CLOSE_ALERT" }),
            addRow,
            deleteRow,
            updateRow,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
        },
    }
}